'use client'

import { useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useUiEventStore } from '@cerifi/state/ui-event.store'

import { cn } from '../../utils/cn'
import { Divider } from '../divider'
import { CloseIcon } from '../icons'

export interface DialogProps {
  title?: React.ReactNode
  children: React.ReactNode
  isOpen: boolean
  maxWidth?: string
  noPadding?: boolean
  hideTitle?: boolean
  trigger?: React.ReactNode
  onClose: () => void
}
const Dialog = (props: DialogProps) => {
  const setIsOverlayOpen = useUiEventStore((state) => state.setIsOverlayOpen)

  const [trigger, setTrigger] = useState<Element | null>()

  useEffect(() => {
    setIsOverlayOpen(props.isOpen)
    if (props.trigger) {
      return
    }
    a11yRefocus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen])

  /** Handles refocusing the element that opened this dialog */
  const a11yRefocus = () => {
    if (props.isOpen && document.activeElement) {
      setTrigger(document.activeElement)
    } else if (!props.isOpen && trigger) {
      ;(trigger as HTMLButtonElement).focus()
    }

    if (!props.isOpen) {
      setTrigger(null)
    }
  }

  return (
    <DialogPrimitive.Root
      open={props.trigger ? undefined : props.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          props.onClose()
        }
      }}
    >
      {props.trigger && (
        <DialogPrimitive.Trigger asChild>
          {props.trigger}
        </DialogPrimitive.Trigger>
      )}

      <DialogPrimitive.Portal>
        <DialogPrimitive.DialogOverlay
          style={{ zIndex: 99 }}
          className="data-[state=open]:animate-overlayShow fixed inset-0 bg-black/[0.8]"
        />

        <DialogPrimitive.DialogContent
          style={{
            maxWidth: props.maxWidth || '800px',
            overflowY: 'auto',
            zIndex: 100,
          }}
          className={cn(
            'data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] translate-x-[-50%] translate-y-[-50%] rounded-md bg-white shadow-xl focus:outline-none'
          )}
        >
          <DialogPrimitive.Title className="sticky top-0 overflow-hidden bg-white">
            <div
              className={cn(
                'flex flex-row',
                props.hideTitle ? 'justify-end' : 'justify-between'
              )}
            >
              <div
                className={cn(
                  'flex p-6 font-semibold',
                  props.hideTitle ? 'sr-only' : ''
                )}
              >
                {props.title ? (
                  <div className="body-font text-lg">{props.title}</div>
                ) : (
                  <div /> /* Spacer */
                )}
              </div>

              <DialogPrimitive.Close
                className="p-3 md:p-6"
                aria-label="close button"
              >
                <CloseIcon />
              </DialogPrimitive.Close>
            </div>
            {props.hideTitle ? '' : <Divider />}
          </DialogPrimitive.Title>

          <div
            className={cn(
              props.noPadding ? '' : 'p-3 md:p-6',
              props.hideTitle ? '' : 'pt-6'
            )}
          >
            {props.children}
          </div>
        </DialogPrimitive.DialogContent>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

Dialog.displayName = 'Dialog'

export { Dialog }
